import {appInstance} from '~/utils/app-accessor'
import {authStore, persistentStore} from '~/composables/store-accessor'
import {
    B2B_AUTH_EVENT,
    CHANGE_LOCALE_EVENT,
    EventBus,
    RESET_BOOKING_DATA,
    RESET as RESET_EVENT,
} from '~/utils/event-bus'
import {orderFilters, ordersResponse as blankOrdersResponse} from '~/utils/store-blank-states'
import {differenceInYears, parseISO} from 'date-fns'
import type {Ref} from 'vue'
import {axiosInstance} from '~/utils/axios-accessor'

export const setupRuntimeStore = () => {
    const loading = ref(false)
    const authActive = ref(false)
    const ordersResponse: Ref = ref(blankOrdersResponse())
    const orderDetails: Ref = ref({})
    const bookingActive = ref(false)
    const conditionsLoading: Ref = ref({})
    const persons: Ref<any[]> = ref([])
    const personsCount: Ref<number> = ref(0)
    const clients: Ref<any[]> = ref([])
    const companies: Ref<any[]> = ref([])
    const companiesCount: Ref<number> = ref(0)
    const companiesFilterKey: Ref = ref(null)
    const touristBookingFields: Ref = ref({bookingFields: {}})
    const productBookingFields = ref({})
    const exchangeRates = ref({})
    const countries: Ref<any[]> = shallowRef([])
    const restrictedCountries: Ref<any[]> = ref([])
    const clientsCount = ref(0)
    // For mod extension (can't extend state params in VuexModule)
    const config = ref({})
    const CRMOptions: Ref = shallowRef([])
    const CRMSettings: Ref = shallowRef(null)
    const availablePersonRoles: Ref = ref([])
    // Track the current client page ID for filtering orders on the client page only
    const currentClientPageId: Ref<number | null> = ref(null)

    function SET_ORDERS_RESPONSE(_ordersResponse: any) {
        const {orders, ...rs} = _ordersResponse
        ordersResponse.value = {orders: orders.map((order: any) => Object.freeze(order)), ...rs}
    }

    function SET_CURRENT_CLIENT_PAGE_ID(clientId: number | null) {
        currentClientPageId.value = clientId
    }

    function SET_CONDITIONS_LOADING({offerKey, cts}: any) {
        conditionsLoading.value[offerKey] = cts
    }

    function SET_BOOKING_ACTIVE(active: any) {
        bookingActive.value = active
    }

    function SET_ORDER_DETAILS(data: any) {
        orderDetails.value = data
    }

    function SET_LOADING(_loading: any) {
        loading.value = _loading
    }

    function SET_PERSONS(_persons: any) {
        persons.value = _persons
    }

    function SET_PERSONS_COUNT(count: any) {
        personsCount.value = count
    }

    function UPDATE_PERSON({index, val}: any) {
        persons.value[index] = val
    }

    function SET_CLIENTS(_clients: any) {
        clients.value = _clients
    }

    function SET_CLIENTS_COUNT(count: any) {
        clientsCount.value = count
    }

    function SET_COMPANIES(_companies: any) {
        companies.value = _companies
    }

    function SET_COMPANIES_COUNT(count: any) {
        companiesCount.value = count
    }

    function SET_COMPANIES_FILTER_KEY(_filterKey: any) {
        companiesFilterKey.value = _filterKey
    }

    function SET_AVAILABLE_PERSON_ROLES(data: any) {
        availablePersonRoles.value = data
    }

    function UPDATE_ORDER({orderId, data}: any) {
        const index = ordersResponse.value.orders.findIndex((order: any) => order.orderId === orderId)
        ordersResponse.value.orders[index] = Object.freeze({...ordersResponse.value.orders[index], ...data})
    }

    function UPDATE_SERVICE_COMMENT({serviceId, comment}: any) {
        const serviceIndex = orderDetails.value.services.findIndex((service: any) => service.serviceId === serviceId)
        orderDetails.value.services[serviceIndex].comments = comment
    }

    function SET_AUTH_ACTIVE(active: any) {
        authActive.value = active
    }

    function RESET() {
        ordersResponse.value = blankOrdersResponse()
        orderDetails.value = {}
        persons.value = []
    }

    function SET_DEFAULT_TOURIST_BOOKING_FIELDS() {
        touristBookingFields.value = {
            bookingFields: {
                BIRTHDATE: {
                    mainTouristStatus: 'DISABLED',
                    allTouristsStatus: 'DISABLED',
                    childrenStatus: 'MANDATORY',
                },
                CITIZENSHIP: {
                    mainTouristStatus: 'DISABLED',
                    allTouristsStatus: 'DISABLED',
                    childrenStatus: 'DISABLED',
                },
                EMAIL: {
                    mainTouristStatus: 'DISABLED',
                    allTouristsStatus: 'DISABLED',
                    childrenStatus: 'DISABLED',
                },
                PASSPORT_EXPIRE_DATE: {
                    mainTouristStatus: 'DISABLED',
                    allTouristsStatus: 'DISABLED',
                    childrenStatus: 'DISABLED',
                },
                PASSPORT_ISSUE_DATE: {
                    mainTouristStatus: 'DISABLED',
                    allTouristsStatus: 'DISABLED',
                    childrenStatus: 'DISABLED',
                },
                PASSPORT_NUMBER: {
                    mainTouristStatus: 'DISABLED',
                    allTouristsStatus: 'DISABLED',
                    childrenStatus: 'DISABLED',
                },
                PHONE: {
                    mainTouristStatus: 'DISABLED',
                    allTouristsStatus: 'DISABLED',
                    childrenStatus: 'DISABLED',
                },
                PREFIX: {
                    mainTouristStatus: 'OPTIONAL',
                    allTouristsStatus: 'OPTIONAL',
                    childrenStatus: 'OPTIONAL',
                },
            },
        }
    }

    // TODO: temporary solution for cars and tours
    function SET_CUSTOM_TOURIST_BOOKING_FIELDS(bookingFields: any) {
        touristBookingFields.value = {bookingFields}
    }

    function SET_TOURIST_BOOKING_FIELDS(_touristBookingFields: any) {
        touristBookingFields.value = _touristBookingFields
    }

    async function loadCountries() {
        try {
            const [{countries: _countries}, {countries: _restrictedCountries}] = await Promise.all([
                appInstance.$api.locations.get({limitCountries: 300, orderBy: 'ALPHABET', applyRestriction: false}),
                appInstance.$api.locations.get({limitCountries: 300, orderBy: 'ALPHABET'}),
            ])
            countries.value = _countries
            restrictedCountries.value = _restrictedCountries
        } catch (e) {
            countries.value = []
            restrictedCountries.value = []
        }
    }

    async function loadExchangeRates(currencyCode: any) {
        if (currencyCode === 'ORIGINAL') currencyCode = appInstance.$conf.public.currency.available[0]
        try {
            exchangeRates.value = await appInstance.$api.exchangeRates.get({
                currencyCode,
                precision: 10,
            })
        } catch (e) {
            exchangeRates.value = {}
        }
    }

    async function loadProductBookingFields({offerKey, processId}: any) {
        try {
            productBookingFields.value = (
                await appInstance.$api.productBookingFields.get({
                    ...(offerKey && {offerKey}),
                    ...(processId && {processId}),
                })
            ).bookingFields
        } catch (e) {
            productBookingFields.value = {}
        }
    }

    async function loadTouristBookingFields({offerKey, processId}: any) {
        try {
            const _touristBookingFields = await appInstance.$api.touristBookingFields.get({
                ...(offerKey && {offerKey}),
                ...(processId && {processId}),
            })
            //TODO It is workaround for set PASSPORT_TYPE
            /*const passportFieldKeys = [
                //'PASSPORT_ISSUE_COUNTRY',
                'PASSPORT_EXPIRE_DATE',
                'PASSPORT_ISSUE_DATE',
                'PASSPORT_NUMBER',
            ]
            const bookingFields = touristBookingFields.bookingFields*/
            //TODO Remove passport type???
            _touristBookingFields.bookingFields.PASSPORT_TYPE = {
                mainTouristStatus: 'DISABLED',
                allTouristsStatus: 'DISABLED',
                childrenStatus: 'DISABLED',
            }
            touristBookingFields.value = _touristBookingFields
        } catch (e: any) {
            touristBookingFields.value = {bookingFields: {}}
            if (e.status === 404) EventBus.$emit('OFFER_EXPIRED_EVENT')
        }
    }

    let cancelTokenSource

    async function loadOrders({rq, update}: any) {
        if (!update) {
            SET_LOADING(true)
        }
        if (cancelTokenSource) {
            cancelTokenSource.cancel()
        }
        cancelTokenSource = axiosInstance.CancelToken.source()
        try {
            const rs = await appInstance.$api.orders.get(rq, cancelTokenSource.token)
            if (rs) {
                // Filter orders by clientId ONLY when viewing a specific client's page
                // This prevents showing orders from other clients with the same name
                if (currentClientPageId.value && rs.orders) {
                    const filteredOrders = rs.orders.filter((order: any) => {
                        // Check if the order's clientId matches the current client page ID
                        return order.clientId === currentClientPageId.value
                    })

                    // Update the response with filtered orders and adjust the count
                    const filteredResponse = {
                        ...rs,
                        orders: filteredOrders,
                        ordersCount: filteredOrders.length,
                    }
                    SET_ORDERS_RESPONSE(filteredResponse)
                } else {
                    SET_ORDERS_RESPONSE(rs)
                }
            }
            return true
        } catch (e) {
            return false
        } finally {
            SET_LOADING(false)
        }
    }

    async function loadOrder(rq: any) {
        SET_ORDER_DETAILS({})
        try {
            const {
                orders: [order],
            } = await appInstance.$api.orders.get(rq)
            SET_ORDER_DETAILS(order)
            return order
        } catch (e) {
            return e
        }
    }

    async function refreshOrder() {
        try {
            const {
                orders: [order],
            } = await appInstance.$api.orders.get({
                orderType: authStore.orderType,
                orderId: orderDetails.value.orderId,
            })
            SET_ORDER_DETAILS(order)
            // eslint-disable-next-line no-empty
        } catch (e) {}
    }

    async function loadPersons(rq: any) {
        SET_LOADING(true)
        try {
            if (rq.active === undefined) {
                const [active, disabled] = await Promise.all([
                    appInstance.$api.persons.get(Object.assign(rq, {active: true})),
                    appInstance.$api.persons.get(Object.assign(rq, {active: false})),
                ])
                SET_PERSONS([...active.users, ...disabled.users])
            } else {
                const persons = await appInstance.$api.persons.get(rq)
                SET_PERSONS(persons.users)
                SET_PERSONS_COUNT(persons.count)
            }
        } catch (e) {
            SET_PERSONS([])
            SET_PERSONS_COUNT(0)
        } finally {
            SET_LOADING(false)
        }
    }

    async function updatePerson(val: any) {
        SET_LOADING(true)
        const {id} = val
        delete val.id
        try {
            //await appInstance.$api.persons.put(id, val)
            const index = persons.value.findIndex((person: any) => person.personId === id)
            UPDATE_PERSON({index, val})
            // eslint-disable-next-line no-empty
        } catch (e) {
        } finally {
            SET_LOADING(false)
        }
    }

    async function loadPrivateClients(rq: any) {
        SET_LOADING(true)
        try {
            if (rq.active === undefined) {
                const [active, disabled] = await Promise.all([
                    appInstance.$api.privateClients.get({...rq, active: true}),
                    appInstance.$api.privateClients.get({...rq, active: false}),
                ])
                SET_CLIENTS([...active.persons, ...disabled.persons])
            } else {
                const clients = await appInstance.$api.privateClients.get(rq)
                SET_CLIENTS_COUNT(clients.personsCount)
                SET_CLIENTS(clients.persons)
            }
        } catch (e) {
            SET_CLIENTS([])
            SET_CLIENTS_COUNT(0)
        } finally {
            SET_LOADING(false)
        }
    }

    async function loadCompanies(rq: any) {
        SET_LOADING(true)
        try {
            const rs = await appInstance.$api.companies.get(rq)
            SET_COMPANIES(rs.companies)
            SET_COMPANIES_COUNT(rs.companiesCount)
            SET_COMPANIES_FILTER_KEY(rs.filterKey)
        } catch (e) {
            SET_COMPANIES([])
            SET_COMPANIES_COUNT(0)
            SET_COMPANIES_FILTER_KEY(null)
        } finally {
            SET_LOADING(false)
        }
    }

    async function loadCRMOptions() {
        try {
            const {options, settings} = await appInstance.$api.aspOption.get({returnSettings: true})
            CRMOptions.value = options
            CRMSettings.value = settings
        } catch (e) {
            CRMOptions.value = []
            CRMSettings.value = null
        }
    }

    function getCRMOption(name: string): boolean {
        return CRMOptions.value.find(option => option.name === name)?.value
    }

    async function serverInit() {
        const defaultCurrency = appInstance.$conf.public.currency.default
        persistentStore.SET_CURRENCY(defaultCurrency)
        await Promise.all([loadExchangeRates(defaultCurrency), loadCountries(), loadCRMOptions()])
    }

    function clientInit() {
        EventBus.$on(CHANGE_LOCALE_EVENT, reload)
        EventBus.$on(RESET_EVENT, reset)
        EventBus.$on(RESET_BOOKING_DATA, resetBookingData)
        EventBus.$on(B2B_AUTH_EVENT, reset)
    }

    async function reload() {
        await loadCountries()
    }

    function reset() {
        persistentStore.SET_TOURISTS([])
        persistentStore.SET_ORDER_FILTERS(orderFilters())
        persistentStore.SET_COMPANY_SETTINGS({})
        persistentStore.SET_CONDITIONS({})
        persistentStore.SET_ITINERARY_VIEW(false)
        persistentStore.SET_SELECTED_ORDER_ID(null)
        persistentStore.SET_SELECTED_COMPANY_AND_AGENT(null)
        persistentStore.SET_SELECTED_PRIVATE_CLIENT(null)
        persistentStore.CLEAR_BASKET_ADD_ONS()
        RESET()
    }

    function resetBookingData() {
        persistentStore.SET_TOURISTS([])
        persistentStore.SET_CONDITIONS({})
    }

    const orderServiceStatus = computed(() => {
        return (status: any) => {
            status = status?.replace(/[\s.]/g, '_').toUpperCase()
            if (status === 'ERROR' && !authStore.isTO1) {
                return 'PENDING'
            } else {
                return status
            }
        }
    })

    const orderStatusColor = computed(() => {
        return (orderStatus: any) => {
            switch (orderStatus) {
                case 'NEW':
                case 'IN_PROGRESS':
                    return 'text-warning'
                case 'COMPLETED':
                case 'COMPLETED_AND_CHECKED':
                case 'DONE':
                    return 'text-success'
                case 'CANCELLED':
                    return 'text-grey'
                case 'QUOTE':
                    return 'text-blue'
                case 'VOUCHERED':
                    return 'text-green-darken-4'
                case 'ARCHIVED':
                    return 'text-blue-darken-4'
                default:
                    return 'text-error'
            }
        }
    })

    const orderServiceStatusColor = computed(() => {
        return (orderServiceStatus: any) => {
            switch (orderServiceStatus) {
                case 'PENDING':
                case 'CONFIRMATION_PENDING':
                case 'CANCELLATION_PENDING':
                case 'ON_CONFIRM':
                case 'CONFIRMATION_PROCESSING':
                case 'MODIFICATION_PENDING':
                case 'PENDING_TICKETING':
                case 'PENDING_TICKETING_TO1L':
                case 'TICKET_ISSUING':
                case 'PRECONFIRMED':
                    return 'text-warning'
                case 'CONFIRMED':
                case 'ISSUED':
                case 'ESTIMATED':
                    return 'text-success'
                case 'CANCELED':
                    return 'text-grey'
                case 'REJECTED':
                case 'ERROR':
                case 'CANCELLATION_REJECTED':
                case 'CANCELED_WITHOUT_FEE':
                    return 'text-error'
                case 'QUOTE':
                    return 'text-blue'
                default:
                    return 'text-warning'
            }
        }
    })

    const isConditionsLoading = computed(() => {
        return (offerKey: any) => {
            if (persistentStore.conditions[offerKey]) return false
            return conditionsLoading.value[offerKey] === undefined || conditionsLoading.value[offerKey]
        }
    })

    const touristFromPerson = computed(() => {
        return ({
            prefix = null,
            firstName = null,
            lastName = null,
            firstNameLocalized = null,
            lastNameLocalized = null,
            email = null,
            contactPhone = null,
            citizenshipId = null,
            personId,
            birthdate = null,
            role,
            passports,
            type = 'adult',
            age,
        }: any) => {
            const locale = appInstance.$i18n.locale.value
            const isCorporateUser = role && role?.includes('corp.')
            const tourist = {
                prefix,
                firstName: isCorporateUser ? firstNameLocalized[locale] || '' : firstName || firstNameLocalized,
                lastName: isCorporateUser ? lastNameLocalized[locale] || '' : lastName || lastNameLocalized,
                citizenshipId,
                email,
                phone: contactPhone,
                birthdate,
                type,
                passport: {
                    type: null,
                    number: null,
                    issueDate: null,
                    expiryDate: null,
                },
                ...(role && role !== 'client' && {personId}),
                ...(age && type === 'child' && {age}),
            }
            if (passports && passports.length) {
                const {type = null, number = null, issueDate = null, validTill: expiryDate = null} = passports[0]
                Object.assign(tourist.passport, {type, number, issueDate, expiryDate})
            }
            return tourist
        }
    })

    const touristFromTraveler = computed(() => {
        return ({
            prefix,
            name: [{firstName, lastName}],
            citizenshipId,
            isChild,
            phone,
            email,
            dateOfBirth: birthdate,
            passports: [passport],
            age,
            personId,
        }: any) => {
            return {
                prefix: prefix || null,
                firstName: firstName || null,
                lastName: lastName || null,
                citizenshipId: citizenshipId || null,
                type: isChild ? 'child' : 'adult',
                ...(!isChild && {phone: phone || null}),
                ...(!isChild && {email: email || null}),
                birthdate: birthdate || null,
                ...(isChild && {
                    age: age || differenceInYears(new Date(), parseISO(birthdate)) || 2,
                }),
                passport: {
                    type: passport?.type || null,
                    number: passport?.number || null,
                    issueDate: passport?.issueDate || null,
                    expiryDate: passport?.expiryDate || null,
                },
                personId,
            }
        }
    })

    const netPrice = computed(() => {
        return ({amount, currency, originalAmount, originalCurrency, taxesAndFeesIncludedSum, serviceFee}: any) => ({
            amount:
                amount -
                (taxesAndFeesIncludedSum ? taxesAndFeesIncludedSum.amount : 0) -
                (serviceFee ? serviceFee.amount : 0),
            currency,
            ...(originalCurrency && {
                originalAmount:
                    originalAmount -
                    (taxesAndFeesIncludedSum && taxesAndFeesIncludedSum.originalAmount
                        ? taxesAndFeesIncludedSum.originalAmount
                        : 0) -
                    (serviceFee && serviceFee.originalAmount ? serviceFee.originalAmount : 0),
                originalCurrency,
            }),
        })
    })

    const isTouristBookingField = computed(() => {
        return (
            fieldKey: any,
            statusKey: string,
            isMainTourist: any,
            isChild: any,
            specifiedTouristBookingFields: any
        ) => {
            const bookingFields =
                specifiedTouristBookingFields?.bookingFields || touristBookingFields.value.bookingFields
            if (!bookingFields[fieldKey]) return statusKey === 'DISABLED'
            const {mainTouristStatus, allTouristsStatus, childrenStatus} = bookingFields[fieldKey]
            return (
                (isMainTourist && mainTouristStatus === statusKey) ||
                (isChild && childrenStatus === statusKey) ||
                (!isMainTourist && !isChild && allTouristsStatus === statusKey)
            )
        }
    })

    const isTouristBookingFieldDisabled = computed(() => {
        return (fieldKey: any, isMainTourist: any, isChild: any, specifiedTouristBookingFields: any) =>
            isTouristBookingField.value(fieldKey, 'DISABLED', isMainTourist, isChild, specifiedTouristBookingFields)
    })

    const isTouristNotRequired = computed(() => {
        return (tourist: any, isMainTourist: any, specifiedTouristBookingFields: any) =>
            (specifiedTouristBookingFields?.supplierConfigDetails || touristBookingFields.value.supplierConfigDetails)
                ?.guestsInformationRequiredType === 'MIXED' &&
            !isMainTourist &&
            !tourist.firstName &&
            !tourist.lastName
    })

    const ownProduct = computed(() => {
        return (supplierCode: any) => supplierCode?.indexOf('company.') === 0
    })

    const isServiceStatusForbidden = computed(() => {
        const forbiddenStatuses = [
            'Error',
            'No show',
            'Rejected',
            'Canceled',
            'Cancellation pending',
            'Confirmation pending',
            'Quote',
        ]
        return (service: any) => forbiddenStatuses.includes(service.status)
    })

    const isServiceVoucherAvailable = computed(() => {
        const allowedFlightStatuses = ['CONFIRMED', 'ISSUED']
        const allowedStatuses = ['CONFIRMED']

        return (service: any) =>
            ((!isServiceFlight.value(service) && allowedStatuses.includes(service.status?.toUpperCase())) ||
                (isServiceFlight.value(service) && allowedFlightStatuses.includes(service.status?.toUpperCase()))) &&
            !isServiceVisa.value(service) &&
            !isBankFee.value(service)
    })

    const isServiceFlight = computed(() => {
        return (service: any) => ['FLIGHT', 'OWNCHARTER'].includes(service.serviceType)
    })

    const isServiceVisa = computed(() => {
        return (service: any) => ['OWNVISA'].includes(service.serviceType)
    })

    const isBankFee = computed(() => {
        return (service: any) => service.serviceDetails?.[0]?.extraServiceProductType === 'BANK_FEE'
    })

    const plainPaymentStatuses = computed(() => {
        return (statuses: any) =>
            statuses.reduce((statuses: string[], status: string) => {
                if (['OVERPAID', 'PAID', 'PARTIAL_PAID'].includes(status)) {
                    statuses.push(status)
                } else if (!statuses.includes('NOT_PAID')) {
                    statuses.push('NOT_PAID')
                }
                return statuses
            }, [])
    })

    async function loadAvailablePersonRoles(companyType, personId): Promise<void> {
        try {
            const {availableRoleGroups} = await appInstance.$api.availablePersonRoles.get({
                ...(companyType && {companyType}),
                ...(personId && {personId}),
            })
            availablePersonRoles.value = availableRoleGroups
                .map(({name, value}) => ({value, title: name}))
                .sort((a, b) => a.title.localeCompare(b.title))
        } catch (e) {
            availablePersonRoles.value = []
        }
    }

    const departments: Ref = ref([])
    const departmentsLocale: Ref = ref()
    async function loadDepartments(): Promise<void> {
        try {
            departmentsLocale.value = appInstance.$i18n.locale.value
            const rs = await appInstance.$api.departments.get({limit: -1})
            departments.value = rs.departments
        } catch (e) {
            departments.value = []
            departmentsLocale.value = null
        }
    }

    const campaigns: Ref = ref([])
    async function loadCampaigns(): Promise<void> {
        try {
            const rs = await appInstance.$api.campaigns.get()
            campaigns.value = rs.campaigns
        } catch (e) {
            campaigns.value = []
        }
    }

    const productVariants: Ref = ref([])
    const productVariantsLocale: Ref = ref()
    async function loadProductVariants(): Promise<void> {
        try {
            productVariantsLocale.value = appInstance.$i18n.locale.value
            const rs = await appInstance.$api.productVariants.get()
            productVariants.value = rs.productVariants
        } catch (e) {
            productVariants.value = []
            productVariantsLocale.value = null
        }
    }

    return {
        loading,
        authActive,
        ordersResponse,
        orderDetails,
        bookingActive,
        conditionsLoading,
        persons,
        personsCount,
        clients,
        companies,
        companiesCount,
        companiesFilterKey,
        touristBookingFields,
        productBookingFields,
        exchangeRates,
        countries,
        restrictedCountries,
        clientsCount,
        config,
        currentClientPageId,
        SET_ORDERS_RESPONSE,
        SET_CONDITIONS_LOADING,
        SET_BOOKING_ACTIVE,
        SET_ORDER_DETAILS,
        SET_LOADING,
        SET_PERSONS,
        UPDATE_PERSON,
        SET_CLIENTS,
        SET_CLIENTS_COUNT,
        SET_COMPANIES,
        SET_COMPANIES_COUNT,
        SET_AVAILABLE_PERSON_ROLES,
        SET_CURRENT_CLIENT_PAGE_ID,
        UPDATE_ORDER,
        UPDATE_SERVICE_COMMENT,
        SET_AUTH_ACTIVE,
        RESET,
        SET_DEFAULT_TOURIST_BOOKING_FIELDS,
        SET_CUSTOM_TOURIST_BOOKING_FIELDS,
        SET_TOURIST_BOOKING_FIELDS,
        loadCountries,
        loadExchangeRates,
        loadProductBookingFields,
        loadTouristBookingFields,
        loadOrders,
        loadOrder,
        refreshOrder,
        loadPersons,
        updatePerson,
        loadPrivateClients,
        loadCompanies,
        serverInit,
        clientInit,
        reload,
        reset,
        resetBookingData,
        orderServiceStatus,
        orderStatusColor,
        orderServiceStatusColor,
        isConditionsLoading,
        touristFromPerson,
        touristFromTraveler,
        netPrice,
        isTouristBookingField,
        isTouristBookingFieldDisabled,
        isTouristNotRequired,
        ownProduct,
        isServiceStatusForbidden,
        isServiceVoucherAvailable,
        isServiceFlight,
        isServiceVisa,
        isBankFee,
        plainPaymentStatuses,
        CRMOptions,
        getCRMOption,
        CRMSettings,
        loadAvailablePersonRoles,
        availablePersonRoles,
        departments,
        departmentsLocale,
        loadDepartments,
        campaigns,
        loadCampaigns,
        productVariants,
        productVariantsLocale,
        loadProductVariants,
    }
}

export const useRuntimeStore = defineStore('runtime', setupRuntimeStore)

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useRuntimeStore, import.meta.hot))
